import { isNonEmpty } from '@sgme/fp';
import { TextInput } from '@sgme/ui';
import { secondaryMarketBatchApiConnector } from 'apiConnector/secondary-market/batch/api';
import classNames from 'classnames';
import { TinySpinner } from 'components/shared/TinySpinner/TinySpinner';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useUserNotification } from 'hooks/useUserNotification';
import { ClipboardEventHandler, FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useShowToasterMessages } from 'routes/secondary-market-batch/grid/hooks/use-show-toaster-messages';
import { CodeTypeSelector } from 'routes/secondary-market-batch/header/code-type-selector';
import { useOrderType } from 'routes/secondary-market-batch/hooks/use-order-type';
import { useSearchType } from 'routes/secondary-market-batch/hooks/use-search-type';
import { toIntlKey } from 'routes/secondary-market-batch/utils';
import { lastValueFrom } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { ProductCodeType } from 'state/secondaryMarket/productSearch';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import { configByCodeType } from 'utils/analytics';
import { getNewGuid } from 'utils/guid';
import { httpClient } from 'utils/http/httpClient';
import { LoadingStatus } from 'utils/status';

const api = secondaryMarketBatchApiConnector(httpClient);
export function parseCode(value: string): { code: string; subType?: string } {
  if (value.includes('-')) {
    const [subType, code] = value.split('-');
    return { code, subType };
  }
  return { code: value };
}

const mapSearchTypeToProductCodeType = (searchType: string): ProductCodeType => {
  if (searchType === 'FILE_CODE') {
    return 'File Code';
  }
  return searchType as ProductCodeType;
};

const useSearchProducts = () => {
  const { orderType } = useOrderType();
  const searchType = useSearchType();
  const dispatch = useThunkDispatch();
  const toast = useUserNotification();
  const [status, setStatus] = useState<LoadingStatus>('idle');
  const showToasterMessages = useShowToasterMessages(orderType);

  const search = useCallback(
    (codes: string[]) => {
      const ids = codes.map(c => ({
        ...parseCode(c),
        source: searchType.source,
        subtype: searchType.subtype,
        id: getNewGuid(),
      }));
      setStatus('loading');
      lastValueFrom(
        api.search(orderType, {
          ids,
        }),
      )
        .then(response => {
          setStatus('success');
          showToasterMessages(response.rows);
          logAnalytics.trackEvent(
            'Secondary market batch',
            configByCodeType[mapSearchTypeToProductCodeType(searchType.source)].action,
          );
          dispatch(secondaryMarketBatchActions.searchSucceeded(orderType, response));
        })
        .catch(error => {
          setStatus('error');
          toast.error({
            title: { id: 'secondaryMarketProductSearch.technicalError.title' },
            content: [{ id: 'secondaryMarketProductSearch.technicalError.content' }],
            closeAutomatically: false,
            minWidth: 500,
          });
          dispatch(secondaryMarketBatchActions.searchFailed(orderType, error));
        });
    },
    [orderType, searchType, dispatch, toast, showToasterMessages],
  );
  return { search, status, searchType };
};

const TechnicalError: FC<{ className?: string }> = ({ className }) => (
  <em className={classNames('icon text-danger icon-md', className)}>error</em>
);

function formatInput(values: string) {
  const regex = /,|;|\n/;
  let items = [];
  if (regex.test(values)) {
    items = values.split(regex).map(x => x.trim()) ?? [];
  } else {
    items = [values.trim()];
  }
  return items.filter(isNonEmpty);
}

type Props = {
  className?: string;
};

export function CodeSearch({ className }: Props) {
  const { search, status, searchType } = useSearchProducts();
  const [value, setValue] = useState('');
  const { formatMessage } = useIntl();

  const onValuePasted: ClipboardEventHandler<HTMLInputElement> = useCallback(
    e => {
      const value = e.clipboardData?.getData('text') ?? '';
      search(formatInput(value));
      setValue('');
      e.preventDefault();
    },
    [search],
  );

  return (
    <div className={classNames('d-flex border-start product-search', className)}>
      <div className="h-100 flex-grow-1 overflow-hidden product-search-input">
        <TextInput
          id="code-search"
          value={value}
          icon="add"
          role="searchbox"
          onPaste={onValuePasted}
          onChange={useCallback(e => {
            setValue(e.currentTarget.value);
          }, [])}
          onKeyDown={useCallback(
            e => {
              if (e.key === 'Enter') {
                search(formatInput(e.currentTarget.value));
                setValue('');
              }
            },
            [search],
          )}
          placeholder={formatMessage(
            { id: 'secondaryMarket.search.placeholder' },
            { value: formatMessage({ id: toIntlKey(searchType) }) },
          )}
          className="h-100"
        />
      </div>

      <div className="d-flex align-items-center product-search-code-type-selector">
        {status === 'error' && <TechnicalError className="mx-2" />}
        {status === 'loading' && <TinySpinner size={1.5} />}
        <CodeTypeSelector />
      </div>
    </div>
  );
}
